import { ProductSliderSources } from "@omni/types/components/product/enums/productSliderResource.enum";
import { productSlider } from "@omni/ui/brand/product/productSlider/productSlider.styles";
import { MappedContextProvider } from "@omni/utils/context/mapped.context";
import { ReactNode, useCallback, useEffect } from "react";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { useProductSliderContext } from "@/components/Product/ProductSlider/context/productSlider.context";
import { useProductSliderSourceContext } from "@/components/Product/ProductSlider/context/productSliderSource.context";
import * as SwiperUtils from "@/utils/swiper.utils";

type Props = {
    className?: string;
    children: ReactNode;
};

export const Base = ({ className, children }: Props) => {
    const { products } = useProductSliderSourceContext();
    const {
        source,
        breakpointsConfig,
        mainSwiper,
        prevButtonRef,
        nextButtonRef,
        setMainSwiper,
        setAllowPrev,
        setAllowNext
    } = useProductSliderContext();

    const spaceBetween = source === ProductSliderSources.Recent ? 8 : 20;

    const variables = SwiperUtils.getLayoutCssVariables({
        layoutSettings: breakpointsConfig.layoutSettings,
        spaceBetween: spaceBetween
    });

    const breakPointSettings = SwiperUtils.getEffectiveLayoutSettings(
        breakpointsConfig.layoutSettings
    );

    const updateButtons = useCallback(() => {
        if (!mainSwiper) return;

        setAllowPrev(!mainSwiper.isBeginning);
        setAllowNext(!mainSwiper.isEnd);
    }, [mainSwiper, setAllowNext, setAllowPrev]);

    useEffect(() => {
        if (!mainSwiper) return;

        updateButtons();
    }, [mainSwiper, updateButtons]);

    const { slider_slides, slider_slides_wrapper, slider_slides_swiperslide } =
        productSlider();

    return (
        <Swiper
            modules={[Navigation]}
            navigation={{
                prevEl: prevButtonRef.current,
                nextEl: nextButtonRef.current
            }}
            updateOnWindowResize={true}
            resizeObserver={true}
            slidesPerView={1}
            slidesPerGroup={1}
            wrapperClass={slider_slides_wrapper()}
            breakpoints={breakPointSettings}
            spaceBetween={spaceBetween}
            className={slider_slides({
                initialized: !!mainSwiper,
                className: className
            })}
            style={variables}
            onSwiper={setMainSwiper}
            onSlideChange={updateButtons}
            onSlidesUpdated={updateButtons}
        >
            {products.map((product, index) => (
                <SwiperSlide
                    key={`${product.sku}-${index}`}
                    className={slider_slides_swiperslide({
                        initialized: !!mainSwiper
                    })}
                >
                    <MappedContextProvider index={index}>
                        {children}
                    </MappedContextProvider>
                </SwiperSlide>
            ))}
        </Swiper>
    );
};
